import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Appointment, AppointmentType, AvailabilitySlot, BlockedDate } from '../mocks/appointments';

export interface SlotOccupancy {
  date: string;
  time: string;
  totalSlots: number;   // suma de max_bookings de los slots que cubren esta hora
  booked: number;       // citas confirmadas/pendientes a esa hora en esa fecha
  available: number;
}

export function useSupabaseAppointments() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [types, setTypes] = useState<AppointmentType[]>([]);
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apptRes, typesRes, slotsRes, blockedRes] = await Promise.all([
        supabase.from('appointments').select('*').order('created_at', { ascending: false }),
        supabase.from('appointment_types').select('*').order('name'),
        supabase.from('availability_slots').select('*').order('day_of_week'),
        supabase.from('blocked_dates').select('*').order('date'),
      ]);

      if (apptRes.data) setAppointments(apptRes.data as Appointment[]);
      if (typesRes.data) setTypes(typesRes.data as AppointmentType[]);
      if (slotsRes.data) setSlots(slotsRes.data as AvailabilitySlot[]);
      if (blockedRes.data) setBlockedDates(blockedRes.data as BlockedDate[]);

      if (apptRes.error) setError(apptRes.error.message);
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ── Slot occupancy helpers ── */
  const timeToMinutes = (time: string): number => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const getOccupancyForDay = useCallback((dateISO: string, dow: number) => {
    const daySlots = slots.filter((s) => s.day_of_week === dow && s.is_active);
    const dayAppointments = appointments.filter(
      (a) => a.preferred_date === dateISO && a.status !== 'cancelled'
    );

    // Build unique time strings from all active slots
    const timeSet = new Set<string>();
    daySlots.forEach((slot) => {
      const [sh, sm] = slot.start_time.split(':').map(Number);
      const [eh, em] = slot.end_time.split(':').map(Number);
      let h = sh;
      let m = sm;
      while (h < eh || (h === eh && m < em)) {
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        timeSet.add(timeStr);
        m += 30;
        if (m >= 60) { m = 0; h += 1; }
      }
    });

    const occupancy: SlotOccupancy[] = [];
    Array.from(timeSet).sort().forEach((time) => {
      const coveringSlots = daySlots.filter(
        (s) => time >= s.start_time && time < s.end_time
      );
      const totalSlots = coveringSlots.reduce((sum, s) => sum + s.max_bookings, 0);

      // Count appointments that OVERLAP with this 30-min slot (respecting service duration)
      const slotStart = timeToMinutes(time);
      const slotEnd = slotStart + 30;
      const booked = dayAppointments.filter((appt) => {
        if (!appt.preferred_time) return false;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        return apptStart < slotEnd && apptEnd > slotStart;
      }).length;

      occupancy.push({ date: dateISO, time, totalSlots, booked, available: Math.max(0, totalSlots - booked) });
    });
    return occupancy;
  }, [slots, appointments, types]);

  const getOccupancyForSlot = useCallback((dateISO: string, timeStr: string, dow: number) => {
    const list = getOccupancyForDay(dateISO, dow);
    return list.find((o) => o.time === timeStr) ?? { date: dateISO, time: timeStr, totalSlots: 0, booked: 0, available: 0 };
  }, [getOccupancyForDay]);

  const addAppointment = useCallback(async (item: Omit<Appointment, 'id' | 'created_at' | 'updated_at'>) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('appointments')
        .insert(item)
        .select()
        .single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) {
        setAppointments((prev) => [data as Appointment, ...prev]);
      }
      return data as Appointment;
    } catch {
      setError('Error al crear');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAppointment = useCallback(async (id: string, updates: Partial<Appointment>) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('appointments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) {
        setAppointments((prev) =>
          prev.map((a) => (a.id === id ? (data as Appointment) : a))
        );
      }
      return data as Appointment;
    } catch {
      setError('Error al actualizar');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAppointment = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const { error: err } = await supabase.from('appointments').delete().eq('id', id);
      if (err) {
        setError(err.message);
        return false;
      }
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      return true;
    } catch {
      setError('Error al eliminar');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const createStripeCheckout = useCallback(async (payload: {
    name: string;
    email: string;
    phone?: string;
    service_type: string;
    preferred_date: string;
    preferred_time: string;
    notes?: string;
    service_name: string;
    service_price: number;
    service_description?: string;
    origin_url: string;
  }) => {
    setLoading(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('stripe-checkout', {
        body: payload,
      });
      if (fnErr) {
        setError(fnErr.message);
        return null;
      }
      if (data?.error) {
        setError(data.error);
        return null;
      }
      return data as { url: string; appointment_id: string };
    } catch {
      setError('Error al iniciar el pago');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const addSlot = useCallback(async (slot: Omit<AvailabilitySlot, 'id' | 'created_at'>) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase.from('availability_slots').insert(slot).select().single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) setSlots((prev) => [...prev, data as AvailabilitySlot]);
      return data as AvailabilitySlot;
    } catch {
      setError('Error al crear slot');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSlot = useCallback(async (id: string, updates: Partial<AvailabilitySlot>) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase.from('availability_slots').update(updates).eq('id', id).select().single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) setSlots((prev) => prev.map((s) => (s.id === id ? (data as AvailabilitySlot) : s)));
      return data as AvailabilitySlot;
    } catch {
      setError('Error al actualizar slot');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSlot = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const { error: err } = await supabase.from('availability_slots').delete().eq('id', id);
      if (err) {
        setError(err.message);
        return false;
      }
      setSlots((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch {
      setError('Error al eliminar slot');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const addBlockedDate = useCallback(async (date: string, reason?: string) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase.from('blocked_dates').insert({ date, reason }).select().single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) setBlockedDates((prev) => [...prev, data as BlockedDate]);
      return data as BlockedDate;
    } catch {
      setError('Error al bloquear fecha');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const removeBlockedDate = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const { error: err } = await supabase.from('blocked_dates').delete().eq('id', id);
      if (err) {
        setError(err.message);
        return false;
      }
      setBlockedDates((prev) => prev.filter((d) => d.id !== id));
      return true;
    } catch {
      setError('Error al desbloquear fecha');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const addType = useCallback(async (type: Omit<AppointmentType, 'id' | 'created_at'>) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase.from('appointment_types').insert(type).select().single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) setTypes((prev) => [...prev, data as AppointmentType]);
      return data as AppointmentType;
    } catch {
      setError('Error al crear tipo');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateType = useCallback(async (id: string, updates: Partial<AppointmentType>) => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase.from('appointment_types').update(updates).eq('id', id).select().single();
      if (err) {
        setError(err.message);
        return null;
      }
      if (data) setTypes((prev) => prev.map((t) => (t.id === id ? (data as AppointmentType) : t)));
      return data as AppointmentType;
    } catch {
      setError('Error al actualizar tipo');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteType = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const { error: err } = await supabase.from('appointment_types').delete().eq('id', id);
      if (err) {
        setError(err.message);
        return false;
      }
      setTypes((prev) => prev.filter((t) => t.id !== id));
      return true;
    } catch {
      setError('Error al eliminar tipo');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    appointments,
    types,
    slots,
    blockedDates,
    loading,
    error,
    addAppointment,
    updateAppointment,
    deleteAppointment,
    createStripeCheckout,
    addSlot,
    updateSlot,
    deleteSlot,
    addBlockedDate,
    removeBlockedDate,
    addType,
    updateType,
    deleteType,
    getOccupancyForDay,
    getOccupancyForSlot,
    refresh: fetchAll,
  };
}